import { useState, useEffect } from "react";
import { ThemeEnum, LS_KEY, DARK_CLASS } from "../enums/common/ThemeEnum";

type ThemeListener = (theme: ThemeEnum) => void;

class ThemeService {
    private listeners: ThemeListener[] = [];
    private currentTheme: ThemeEnum = ThemeEnum.LIGHT;

    constructor() {
        if (typeof window !== "undefined") {
            const storedTheme =
                (localStorage.getItem(LS_KEY) as ThemeEnum) || ThemeEnum.LIGHT;
            this.currentTheme = storedTheme;
            this.applyTheme(storedTheme);
        }
    }

    private applyTheme(theme: ThemeEnum) {
        if (theme === ThemeEnum.DARK) {
            document.body.classList.add(DARK_CLASS);
        } else {
            document.body.classList.remove(DARK_CLASS);
        }
    }

    public getTheme() {
        return this.currentTheme;
    }

    public toggleTheme() {
        const newTheme =
            this.currentTheme === ThemeEnum.DARK
                ? ThemeEnum.LIGHT
                : ThemeEnum.DARK;
        this.setTheme(newTheme);
    }

    public setTheme(theme: ThemeEnum) {
        this.currentTheme = theme;
        localStorage.setItem(LS_KEY, theme);
        this.applyTheme(theme);
        this.notifyListeners(theme);
    }

    public subscribe(listener: ThemeListener) {
        this.listeners.push(listener);
        listener(this.currentTheme);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== listener);
        };
    }

    private notifyListeners(theme: ThemeEnum) {
        this.listeners.forEach((listener) => listener(theme));
    }
}

export const themeService = new ThemeService();

export const useTheme = () => {
    const [theme, setTheme] = useState(themeService.getTheme());

    useEffect(() => {
        const unsubscribe = themeService.subscribe(setTheme);
        return unsubscribe;
    }, []);

    return {
        theme,
        toggleTheme: () => themeService.toggleTheme(),
        setTheme: (t: ThemeEnum) => themeService.setTheme(t),
        isDark: theme === ThemeEnum.DARK,
        isLight: theme === ThemeEnum.LIGHT,
    };
};
